// ---------------------------------------------------------------------------
// TenantConfigForm — React Hook Form + Zod for tenant configuration editing
// ---------------------------------------------------------------------------

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { EffectiveTenantConfig, UpdateTenantConfigRequest } from '@/types/config';

// -- Zod schemas --

const callWindowSchema = z.object({
  fromHour: z.coerce.number().int().min(0).max(23),
  fromMinute: z.coerce.number().int().min(0).max(59),
  toHour: z.coerce.number().int().min(0).max(23),
  toMinute: z.coerce.number().int().min(0).max(59),
}).refine(
  (w) => w.fromHour * 60 + w.fromMinute < w.toHour * 60 + w.toMinute,
  { message: 'Start time must be before end time' },
);

const tenantConfigSchema = z.object({
  ivrEnabled: z.boolean(),
  noWeekendCalls: z.boolean(),
  maxQuestionsInCall: z.coerce.number().int().min(1).max(100),
  callWindows: z.object({
    regular: callWindowSchema,
    event: callWindowSchema,
    alert: callWindowSchema,
    scheduled: callWindowSchema,
  }),
  retryConfig: z.object({
    callBackAttempts: z.coerce.number().int().min(0).max(10),
    callBackInterval: z.coerce.number().int().min(1).max(480),
    nextDayCallBackAttempts: z.coerce.number().int().min(0).max(5),
    nextDayCallBackInterval: z.coerce.number().int().min(1).max(480),
  }),
});

type TenantConfigValues = z.infer<typeof tenantConfigSchema>;

const WINDOW_LABELS: Record<string, string> = {
  regular: 'Regular',
  event: 'Event-Triggered',
  alert: 'Alert-Triggered',
  scheduled: 'Scheduled',
};

type WindowKey = 'regular' | 'event' | 'alert' | 'scheduled';

interface TenantConfigFormProps {
  config: EffectiveTenantConfig;
  onSubmit: (values: UpdateTenantConfigRequest) => void;
  onCancel: () => void;
  isPending: boolean;
}

export function TenantConfigForm({ config, onSubmit, onCancel, isPending }: TenantConfigFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TenantConfigValues>({
    resolver: zodResolver(tenantConfigSchema),
    defaultValues: {
      ivrEnabled: config.ivrEnabled,
      noWeekendCalls: config.noWeekendCalls,
      maxQuestionsInCall: config.maxQuestionsInCall,
      callWindows: config.callWindows,
      retryConfig: config.retryConfig,
    },
  });

  const submit = (values: TenantConfigValues) => {
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-4">
      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              id="ivrEnabled"
              type="checkbox"
              {...register('ivrEnabled')}
              className="h-4 w-4 rounded border-silver-300 text-purple-600 focus:ring-purple-500"
            />
            <Label htmlFor="ivrEnabled" className="mb-0">IVR Enabled</Label>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="noWeekendCalls"
              type="checkbox"
              {...register('noWeekendCalls')}
              className="h-4 w-4 rounded border-silver-300 text-purple-600 focus:ring-purple-500"
            />
            <Label htmlFor="noWeekendCalls" className="mb-0">No Weekend Calls</Label>
          </div>

          <div className="max-w-xs">
            <Label htmlFor="maxQuestionsInCall" className="mb-1">Max Questions per Call</Label>
            <Input
              id="maxQuestionsInCall"
              type="number"
              min={1}
              max={100}
              {...register('maxQuestionsInCall')}
            />
            {errors.maxQuestionsInCall && (
              <p className="mt-1 text-xs text-red-600">{errors.maxQuestionsInCall.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Call Windows */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Call Windows</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {(['regular', 'event', 'alert', 'scheduled'] as WindowKey[]).map((key) => (
            <CallWindowEditor
              key={key}
              label={WINDOW_LABELS[key]}
              windowKey={key}
              register={register}
              errorMessage={errors.callWindows?.[key]?.root?.message}
            />
          ))}
        </CardContent>
      </Card>

      {/* Retry Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Retry Policy</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="callBackAttempts" className="mb-1">Same-Day Attempts (0-10)</Label>
              <Input
                id="callBackAttempts"
                type="number"
                min={0}
                max={10}
                {...register('retryConfig.callBackAttempts')}
              />
              {errors.retryConfig?.callBackAttempts && (
                <p className="mt-1 text-xs text-red-600">{errors.retryConfig.callBackAttempts.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="callBackInterval" className="mb-1">Same-Day Interval (1-480 min)</Label>
              <Input
                id="callBackInterval"
                type="number"
                min={1}
                max={480}
                {...register('retryConfig.callBackInterval')}
              />
              {errors.retryConfig?.callBackInterval && (
                <p className="mt-1 text-xs text-red-600">{errors.retryConfig.callBackInterval.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="nextDayCallBackAttempts" className="mb-1">Next-Day Attempts (0-5)</Label>
              <Input
                id="nextDayCallBackAttempts"
                type="number"
                min={0}
                max={5}
                {...register('retryConfig.nextDayCallBackAttempts')}
              />
              {errors.retryConfig?.nextDayCallBackAttempts && (
                <p className="mt-1 text-xs text-red-600">{errors.retryConfig.nextDayCallBackAttempts.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="nextDayCallBackInterval" className="mb-1">Next-Day Interval (1-480 min)</Label>
              <Input
                id="nextDayCallBackInterval"
                type="number"
                min={1}
                max={480}
                {...register('retryConfig.nextDayCallBackInterval')}
              />
              {errors.retryConfig?.nextDayCallBackInterval && (
                <p className="mt-1 text-xs text-red-600">{errors.retryConfig.nextDayCallBackInterval.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </form>
  );
}

// -- CallWindowEditor sub-component --

interface CallWindowEditorProps {
  label: string;
  windowKey: WindowKey;
  register: ReturnType<typeof useForm<TenantConfigValues>>['register'];
  errorMessage?: string;
}

function CallWindowEditor({ label, windowKey, register, errorMessage }: CallWindowEditorProps) {
  const prefix = `callWindows.${windowKey}` as const;

  return (
    <div>
      <p className="text-sm font-medium text-carbon-700 mb-2">{label}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label htmlFor={`${prefix}.fromHour`} className="mb-1 text-xs text-carbon-500">From Hour</Label>
          <Input id={`${prefix}.fromHour`} type="number" min={0} max={23} {...register(`${prefix}.fromHour`)} />
        </div>
        <div>
          <Label htmlFor={`${prefix}.fromMinute`} className="mb-1 text-xs text-carbon-500">From Min</Label>
          <Input id={`${prefix}.fromMinute`} type="number" min={0} max={59} {...register(`${prefix}.fromMinute`)} />
        </div>
        <div>
          <Label htmlFor={`${prefix}.toHour`} className="mb-1 text-xs text-carbon-500">To Hour</Label>
          <Input id={`${prefix}.toHour`} type="number" min={0} max={23} {...register(`${prefix}.toHour`)} />
        </div>
        <div>
          <Label htmlFor={`${prefix}.toMinute`} className="mb-1 text-xs text-carbon-500">To Min</Label>
          <Input id={`${prefix}.toMinute`} type="number" min={0} max={59} {...register(`${prefix}.toMinute`)} />
        </div>
      </div>
      {errorMessage && (
        <p className="mt-1 text-xs text-red-600">{errorMessage}</p>
      )}
    </div>
  );
}
